/**
 * "Today" as the person using the app sees it: their local calendar date, as
 * YYYY-MM-DD.
 *
 * NOT `new Date().toISOString().slice(0, 10)`. That is the UTC date, and after
 * 8 PM Eastern it is already tomorrow's — so a form that defaulted to it booked
 * tonight's deposit, lease end or aging "as of" on the wrong day, which is the
 * web half of what api#746 fixed on the server.
 *
 * `timeZone` names a zone to read the date in; left out, it is the browser's
 * own. It exists so the conversion can be pinned — and tested — without
 * depending on the zone of whatever machine runs the code.
 *
 * A `Date` built from a date-only string is the opposite trap: it is UTC
 * midnight, the previous evening here. This helper is only for dates that came
 * from a clock, never for turning a stored date back into a string.
 */
export function localISODate(date: Date = new Date(), timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find(entry => entry.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** The local YYYY-MM, for "this month" in the same sense. */
export function localISOMonth(date: Date = new Date(), timeZone?: string): string {
  return localISODate(date, timeZone).slice(0, 7);
}

/**
 * The other direction: a stored calendar date (YYYY-MM-DD) as a `Date` at
 * local midnight on that day.
 *
 * NOT `new Date("2024-01-15")`. A date-only string parses as UTC midnight, so
 * anywhere west of Greenwich it renders as the 14th — every transaction row
 * showed the day before it happened. Anything that isn't a bare date falls
 * through to the normal parser.
 */
export function parseLocalDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return new Date(value);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
